import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  getGetApiV5FirmsRfiRequestsUuidQueryKey,
  usePostApiV5FirmsRfiRequestsUuidUnarchive,
  parseApiErrorMessage,
} from '@liscio/api';
import { Dialog, Typography } from '@liscio/ui';

export interface RfiUnarchiveDialogProps {
  open: boolean;
  requestUuid: string;
  onClose: () => void;
  onSuccessfulUnarchive?: () => void;
}

export const RfiUnarchiveDialog: React.FC<RfiUnarchiveDialogProps> = ({
  open,
  requestUuid,
  onClose,
  onSuccessfulUnarchive,
}) => {
  const queryClient = useQueryClient();

  const { mutateAsync: unarchiveRequest, isLoading } =
    usePostApiV5FirmsRfiRequestsUuidUnarchive({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['/api/v5/firms/rfi/requests'],
          });
          queryClient.invalidateQueries({
            queryKey: getGetApiV5FirmsRfiRequestsUuidQueryKey(requestUuid),
          });
          toast.success('Request unarchived successfully.');
          onSuccessfulUnarchive?.();
        },
        onError: (error) => {
          const errorMessage =
            parseApiErrorMessage(error) || 'Error unarchiving request';
          toast.error(errorMessage);
          console.error('Error unarchiving request:', error);
        },
        onSettled: () => {
          onClose();
        },
      },
    });

  const handleUnarchive = () => {
    unarchiveRequest({ uuid: requestUuid });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Unarchive this request?"
      iconClose
      onSubmit={handleUnarchive}
      submitLabel="Yes, unarchive"
      submitBtnProps={{ color: 'primary', disabled: isLoading }}
      cancelLabel="No, cancel"
      onCancel={onClose}
      cancelBtnProps={{ color: 'primary', disabled: isLoading }}
      aria-label="Unarchive request dialog"
    >
      <Typography>
        Are you sure you want to unarchive this request? It will be restored to
        its previous status.
      </Typography>
    </Dialog>
  );
};

export default RfiUnarchiveDialog;
